import { LocalNotifications } from "@capacitor/local-notifications";

/**
 * Verifica y solicita permisos de notificaciones.
 */
export async function checkAndRequestPermissions() {
    // console.log("🔍 Checking notification permissions...");
    const { display } = await LocalNotifications.checkPermissions();
    if (display === "granted") return true;
    const request = await LocalNotifications.requestPermissions();
    return request.display === "granted";
}

/**
 * Programa un recordatorio de hidratación en X segundos.
 */
export async function scheduleHydrationReminder(seconds: number = 10) {
    await LocalNotifications.schedule({
        notifications: [
            {
                id: Date.now(),
                title: "💧 ¡Hora de beber agua!",
                body: "Recuerda mantenerte hidratado.",
                schedule: { at: new Date(Date.now() + seconds * 1000) },
            },
        ],
    });
}

/**
 * Programa un recordatorio diario a una hora concreta.
 * @param hour Hora del día (0-23)
 * @param minute Minuto (0-59)
 */
export async function scheduleDailyReminder(hour: number, minute: number) {
    await LocalNotifications.schedule({
        notifications: [
            {
                id: 1001,
                title: "📔 Revisa tu progreso",
                body: "¿Ya registraste tu comida y actividad de hoy?",
                schedule: {
                    on: { hour, minute },
                    repeats: true,
                },
            },
        ],
    });
}

/**
 * Programa una notificación personalizada en un momento concreto.
 */
export async function scheduleCustomNotification(
    title: string,
    body: string,
    date: Date,
    id: number = Date.now()
) {
    await LocalNotifications.schedule({
        notifications: [
            {
                id,
                title,
                body,
                schedule: { at: date },
            },
        ],
    });
}

/**
 * Programa un recordatorio diario de racha a las 20:00.
 */
export async function scheduleStreakReminder() {
    const now = new Date();
    const hour = 20;
    const nextReminder = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hour,
        0,
        0
    );
    if (now.getHours() >= hour) {
        nextReminder.setDate(nextReminder.getDate() + 1);
    }

    await LocalNotifications.schedule({
        notifications: [
            {
                id: 101,
                title: "🔥 ¡No pierdas tu racha!",
                body: "Registra tu actividad de hoy y mantén tu progreso.",
                schedule: { at: nextReminder },
            },
        ],
    });
}

/**
 * Cancela todas las notificaciones programadas.
 */
export async function cancelAllNotifications() {
    await LocalNotifications.cancel({ notifications: [] });
}

/**
 * Cancela una notificación concreta por ID.
 */
export async function cancelNotificationById(id: number) {
    await LocalNotifications.cancel({ notifications: [{ id }] });
}

/**
 * Lista todas las notificaciones pendientes.
 */
export async function listPendingNotifications() {
    const pending = await LocalNotifications.getPending();
    console.log("🔔 Pending notifications:", pending.notifications);
    return pending.notifications;
}

import appStore from "@/utils/store";
import { translate } from "@/utils/format";

/**
 * Programa notificaciones diarias para recordar trackear las comidas.
 */
export async function scheduleTrackingReminders() {
    const phenotype = appStore.get("userPhenotype");
    if (!phenotype || !phenotype.mealSchedule) {
        console.warn("⚠️ No hay datos de mealSchedule para programar recordatorios.");
        return;
    }

    const notifications = phenotype.mealSchedule.map(([time, meal], index) => {
        const [hourStr, minuteStr] = time.split(":");
        const hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);

        return {
            id: 200 + index, // IDs únicos por comida
            title: `🍽 ${translate(meal, meal)} ${translate("REMINDER_PENDING", "pendiente")}`,
            body: `${translate("REMINDER_BODY", "No olvides registrar tu ")} ${translate(meal, meal)}`,
            schedule: {
                on: { hour, minute },
                repeats: true,
            },
        };
    });

    console.log("✅ Programando notificaciones de comidas:", notifications);

    await LocalNotifications.schedule({
        notifications,
    });
}

/**
 * Helper para capitalizar texto.
 */
function capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}